/**
 * The employee's own attendance record.
 *
 * Shows the arithmetic rather than just a verdict — "12 of 16 check-ins" next
 * to "75%" — because an availability number that decides pay must be
 * challengeable, and you cannot challenge a figure you cannot see derived.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Eye, Loader2, ShieldAlert } from 'lucide-react';
import { client } from '../api/client';
import ShiftBar from '../components/ShiftBar';
import AttendanceDetailPanel from '../components/AttendanceDetailPanel';
import type { AttendanceDay, Page, Ping } from '../types';
import { PASSED_HINT } from '../lib/checkIns';
import { isWithdrawn, WITHDRAWN_HINT, WITHDRAWN_LABEL } from '../lib/withdrawn';

const STATUS_TONE: Record<AttendanceDay['status'], string> = {
  present: 'bg-emerald-100 text-emerald-700',
  half_day: 'bg-amber-100 text-amber-700',
  absent: 'bg-red-100 text-red-700',
  leave: 'bg-blue-100 text-blue-700',
  holiday: 'bg-gray-100 text-gray-600',
  weekly_off: 'bg-gray-100 text-gray-600',
  // Slate, not red and not green: nothing was measured, so the badge
  // must not read as either a good day or a bad one.
  unmonitored: 'bg-slate-100 text-slate-600',
};

/**
 * Minutes as hours and minutes.
 *
 * Zero renders as a dash rather than "0h 0m": a shift still open has no worked
 * time yet, and a hard zero reads as a claim that nothing was done.
 */
function formatWorked(minutes: number): string {
  if (!minutes) return '—';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

export default function AttendancePage() {
  /**
   * Which slice of history the table is showing.
   *
   * Three granularities because three questions get asked: "what happened on
   * the 12th", "how was September", "how was the year". A month is the one
   * people actually ask for, so it is the default -- the old fixed "last 31
   * days" answered it by accident and could not answer either of the others.
   *
   * Held as a granularity plus an anchor rather than two dates. A from/to
   * pair lets somebody pick a range that means nothing (a Tuesday to the
   * previous Monday) and then read a total over it.
   */
  const [span, setSpan] = useState<'day' | 'month' | 'year'>('month');
  const [anchor, setAnchor] = useState(() => new Date().toISOString().slice(0, 10));

  /** Which of the caller's own days is open, by date. */
  const [openDay, setOpenDay] = useState<string | null>(null);

  const range = (() => {
    const [y, m] = anchor.split('-').map(Number);
    if (span === 'day') return { dateFrom: anchor, dateTo: anchor };
    if (span === 'year') return { dateFrom: `${y}-01-01`, dateTo: `${y}-12-31` };
    // A month, ending on its own last day: day 0 of the next month is the
    // last of this one, which avoids a 31-day table on a 30-day month.
    const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    return { dateFrom: `${anchor.slice(0, 7)}-01`, dateTo: last };
  })();

  const { data: attendance, isLoading } = useQuery<Page<AttendanceDay>>({
    queryKey: ['my-attendance', range.dateFrom, range.dateTo],
    queryFn: async () =>
      (
        await client.get('/api/v1/attendance/me', {
          // 200 is the endpoint's cap and a year of working days fits inside
          // it. Paging a year of one person's own attendance would be a
          // control for nothing.
          params: { ...range, limit: 200 },
        })
      ).data,
  });

  const { data: todaysPings } = useQuery<Ping[]>({
    queryKey: ['todays-pings'],
    queryFn: async () => (await client.get('/api/v1/attendance/ping/today')).data,
  });

  const rows = attendance?.items ?? [];
  /**
   * Hours on the clock over the window shown.
   *
   * Counted over days that produced any, so the average is hours per day
   * worked rather than per calendar day — a weekend in the window would
   * otherwise drag it down and describe nothing.
   */
  const totalWorked = rows.reduce((sum, day) => sum + day.workedMinutes, 0);
  const daysWorked = rows.filter((day) => day.workedMinutes > 0).length;

  return (
    <div className="space-y-6">
      <ShiftBar />

      {todaysPings && todaysPings.length > 0 && (
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">
            Today's Check-ins
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {todaysPings.map((ping) => (
              <span
                key={ping.id}
                title={`${new Date(ping.dueAt).toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                })} — ${ping.status}`}
                className={`h-7 rounded px-2 text-xs leading-7 ${
                  ping.status === 'responded'
                    ? 'bg-emerald-100 text-emerald-700'
                    : ping.status === 'missed'
                      ? 'bg-red-100 text-red-700'
                      : ping.status === 'skipped'
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-gray-100 text-gray-500'
                }`}
              >
                {new Date(ping.dueAt).toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Times are set by the server and vary within each 15-minute slot,
            so a check-in cannot be predicted or waited for.
          </p>
        </section>
      )}

      <section>
        {/* The filter sits with the heading it changes, because it *is*
            the heading now -- the table used to say "Last 31 Days" and mean
            it, and there was no way to ask about any other stretch. */}
        <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
          {/* Both controls built the way every other filter bar here is:
              a fixed width on the *label* and `w-full` on the control, which
              is what puts the label above rather than beside it. Setting
              `block` on the label does not do it -- the text and an
              inline-block select still share a line if the select is narrow
              enough to fit, which is what left "Show" sitting against its
              box. */}
          <div className="flex flex-wrap items-end gap-4">
            <label className="w-32 text-xs text-gray-600">
              Show
              <select
                className="input mt-1 w-full py-1 text-xs"
                value={span}
                onChange={(e) => setSpan(e.target.value as 'day' | 'month' | 'year')}
              >
                <option value="day">One day</option>
                <option value="month">A month</option>
                <option value="year">A year</option>
              </select>
            </label>
            <label className="w-44 text-xs text-gray-600">
              {span === 'day' ? 'Date' : span === 'month' ? 'Month' : 'Year'}
              {/* A real date input, and **no icon of our own**. The browser
                  draws its own calendar button inside the field and that one
                  opens the picker; adding a second was two icons side by
                  side, one of them decorative and dead.

                  `month` for a month, which gives a month-and-year picker
                  rather than asking for a day nobody cares about. The value
                  stays a full ISO date either way, which is what the API
                  wants. */}
              <input
                type={span === 'month' ? 'month' : 'date'}
                className="input mt-1 w-full py-1 text-xs"
                value={span === 'month' ? anchor.slice(0, 7) : anchor}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  setAnchor(span === 'month' ? `${v}-01` : v);
                }}
              />
            </label>
          </div>
          {/* Summed from the same worked_minutes each row shows, so the total
              and the rows can never disagree. Overlapping sessions were
              already merged server-side when each day was rolled up, which is
              why this is a plain sum and not a second calculation.

              Days still running contribute nothing: a shift is not counted
              until it is clocked out, so today's hours appear tomorrow rather
              than creeping upward all afternoon. */}
          {rows.length > 0 && (
            <p className="pb-1 text-xs text-gray-500">
              <span className="font-medium text-gray-900">
                {formatWorked(totalWorked)}
              </span>{' '}
              across {daysWorked} {daysWorked === 1 ? 'day' : 'days'}
              {daysWorked > 0 && (
                <> · {formatWorked(Math.round(totalWorked / daysWorked))} average</>
              )}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <caption className="sr-only">
                Your attendance for the selected period
              </caption>
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Worked</th>
                  <th className="px-4 py-2.5 font-medium">Availability</th>
                  <th className="px-4 py-2.5 font-medium">Check-ins</th>
                  {/* Its own column rather than another parenthesis after the
                      fraction: a meeting-heavy day is the common reason a
                      fraction looks bad, and it should be readable without
                      opening the day. */}
                  <th className="px-4 py-2.5 font-medium" title={PASSED_HINT}>
                    Passed
                  </th>
                  <th className="px-4 py-2.5 font-medium">Window</th>
                  {/* The click used to be on the row and nothing said so, so
                      nobody found it. A labelled control in its own column
                      is the affordance; the row stays clickable for anybody
                      who does try it. */}
                  <th className="px-4 py-2.5 font-medium">Check-in list</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attendance?.items.map((day) => (
                  <tr
                    key={day.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setOpenDay(day.workDate)}
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-900">
                      {day.workDate}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`badge ${STATUS_TONE[day.status]}`}>
                        {day.status.replace(/_/g, ' ')}
                      </span>
                      {/* Moved here when the On site column went. Flags are
                          surfaced for review and never auto-punish, so losing
                          them with the geofence figure would have dropped a
                          separate signal without saying so. */}
                      {day.anomalyFlags && day.anomalyFlags.length > 0 && (
                        <span
                          className="ml-1 inline-flex"
                          title={day.anomalyFlags.join(', ')}
                        >
                          <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
                        </span>
                      )}
                      {day.isManualOverride && (
                        <span className="badge ml-1 bg-indigo-100 text-indigo-700">
                          adjusted
                        </span>
                      )}
                    </td>
                    {/* Hours on the clock, which is not the same question as
                        availability — an unmonitored day has no check-ins to
                        score and can still be a full day's work. */}
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-900">
                      {formatWorked(day.workedMinutes)}
                    </td>
                    <td className="px-4 py-2.5">
                      {isWithdrawn(day) ? (
                        // A bar is a number drawn sideways, so it goes too.
                        <span
                          className="text-xs text-amber-700"
                          title={day.scoringWithdrawnReason ?? WITHDRAWN_HINT}
                        >
                          {WITHDRAWN_LABEL}
                        </span>
                      ) : (
                        <AvailabilityBar pct={Number(day.availabilityPct)} />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {day.respondedPings}/{day.expectedPings}
                      {day.missedPings > 0 && (
                        <span className="ml-1 text-red-500">
                          ({day.missedPings} missed)
                        </span>
                      )}
                    </td>
                    <td
                      className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600"
                      title={PASSED_HINT}
                    >
                      {day.excusedPings ? day.excusedPings : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                      {day.firstActivityAt
                        ? `${time(day.firstActivityAt)} – ${time(day.lastActivityAt)}`
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <button
                        type="button"
                        className="btn-ghost py-1 text-xs"
                        onClick={() => setOpenDay(day.workDate)}
                        title={`Every check-in on ${day.workDate}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {attendance?.items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      {/* Says which period is empty, not just that something
                          is: with a filter above it, "nothing recorded" reads
                          as a broken page rather than as an answer. */}
                      Nothing recorded for{' '}
                      {span === 'day'
                        ? anchor
                        : span === 'month'
                          ? anchor.slice(0, 7)
                          : anchor.slice(0, 4)}
                      . Start a shift to begin.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Their own day, through the self-scoped route -- see the panel's
          `mine` prop for why that is a different endpoint. Keyed by date so
          opening a second day reloads rather than showing the first one. */}
      {openDay && (
        <AttendanceDetailPanel
          key={openDay}
          mine
          userId=""
          workDate={openDay}
          onClose={() => setOpenDay(null)}
        />
      )}
    </div>
  );
}

function AvailabilityBar({ pct }: { pct: number }) {
  // The owner's bands, so the colour and the status agree: green from 65%,
  // amber from 40%. It read green at 75% while the status said present at
  // 65%, which put a whole band of days in amber beside the word "present".
  const tone =
    pct >= 65 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="tabular-nums text-gray-700">{pct.toFixed(0)}%</span>
    </div>
  );
}

function time(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
